import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import { scroller } from "react-scroll";
import { FaFacebookF, FaInstagram, FaLinkedinIn } from "react-icons/fa";
import Flag from "react-world-flags";

import logo from "../asset/images/whiteLogo.png";

const Footer = () => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (location.state?.scrollTo) {
      scroller.scrollTo(location.state.scrollTo, {
        duration: 800,
        delay: 0,
        smooth: "easeInOutQuart",
        offset: -80,
      });
    }
  }, [location.state]);

  // ✅ Same pattern as Header's scrollToSection
  const handleScroll = (sectionId) => {
    if (location.pathname === "/") {
      scroller.scrollTo(sectionId, {
        duration: 800,
        delay: 0,
        smooth: "easeInOutQuart",
        offset: -80,
      });
    } else {
      navigate("/", { state: { scrollTo: sectionId } });
    }
  };

  return (
    <motion.footer
      initial={{ opacity: 0, y: 100 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      viewport={{ once: true }}
      className="bg-[#1b2f5a] text-white px-5 md:px-20 py-8 flex flex-col items-center justify-center font-sans"
    >
      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-4 gap-10 md:gap-16 w-full">
        {/* Company Info */}
        <div className="space-y-4">
          <img src={logo} alt="Company Logo" className="h-8 mb-2" />
          <p className="text-sm text-gray-300 leading-relaxed">
            BytesFarms Technologies is a trusted IT partner founded with 20+
            years of industry expertise.
          </p>
          <h3 className="text-[#F7941D] font-semibold text-md uppercase">
            Feel free to connect with us
          </h3>
          <div className="flex gap-4 text-white text-lg mt-4">
            <motion.a
              href="https://www.facebook.com/share/171jjyYZBE/?mibextid=wwXIfr"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.2 }}
              className="hover:text-[#F7941D] transition-colors"
            >
              <FaFacebookF />
            </motion.a>
            <motion.a
              href="https://www.instagram.com/bytesfarms?igsh=MTQ2MjU2NWVnYWpzMQ=="
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.2 }}
              className="hover:text-[#F7941D] transition-colors"
            >
              <FaInstagram />
            </motion.a>
            <motion.a
              href="https://www.linkedin.com/company/bytesfarmstechnologies/"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.2 }}
              className="hover:text-[#F7941D] transition-colors"
            >
              <FaLinkedinIn />
            </motion.a>
          </div>
        </div>

        {/* Services */}
        <div className="space-y-4">
          <h3 className="text-[#F7941D] font-semibold text-lg">SERVICES</h3>
          <ul className="space-y-2 text-sm text-gray-300">
            {[
              { name: "Generative AI", path: "/services/gen-ai/" },
              { name: "Mobile App Development", path: "/services/mobile-app-development" },
              { name: "Web Development", path: "/services/web-development" },
              { name: "DevOps Consulting", path: "/services/devops-consulting" },
              { name: "Salesforce Development", path: "/services/Salesforce-services" },
            ].map((item) => (
              <li key={item.name}>
                <motion.button
                  onClick={() => navigate(item.path)}
                  whileHover={{ x: 5 }}
                  className={`text-left transition bg-transparent border-none p-0 ${
                    location.pathname === item.path
                      ? "text-[#F7941D] cursor-default"
                      : "hover:text-[#F7941D] cursor-pointer"
                  }`}
                >
                  {item.name}
                </motion.button>
              </li>
            ))}
          </ul>
        </div>

        {/* Quick Links */}
        <div className="space-y-4">
          <h3 className="text-[#F7941D] font-semibold text-lg">QUICK LINKS</h3>
          <ul className="space-y-2 text-sm text-gray-300">

            {/* ✅ Portfolio — now works from any route */}
            <li>
              <motion.button
                whileHover={{ x: 5 }}
                onClick={() => handleScroll("portfolio")}
                className="text-left transition bg-transparent border-none p-0 hover:text-[#F7941D] cursor-pointer"
              >
                Portfolio
              </motion.button>
            </li>

            {/* Company Page */}
            <li>
              <motion.button
                whileHover={{ x: 5 }}
                onClick={() => {
                  if (location.pathname !== "/AboutUs/Company") {
                    navigate("/AboutUs/Company");
                  }
                }}
                className={`text-left transition bg-transparent border-none p-0 ${
                  location.pathname === "/AboutUs/Company"
                    ? "text-[#F7941D] cursor-default"
                    : "hover:text-[#F7941D] cursor-pointer"
                }`}
              >
                Company
              </motion.button>
            </li>

            {/* Meet Our Team */}
            <li>
              <motion.button
                whileHover={{ x: 5 }}
                onClick={() => {
                  if (location.pathname !== "/AboutUs/TeamSection") {
                    navigate("/AboutUs/TeamSection");
                  }
                }}
                className={`text-left transition bg-transparent border-none p-0 ${
                  location.pathname === "/AboutUs/TeamSection"
                    ? "text-[#F7941D] cursor-default"
                    : "hover:text-[#F7941D] cursor-pointer"
                }`}
              >
                Meet Our Team
              </motion.button>
            </li>

            {/* ✅ Contact Us — now works from any route */}
            <li>
              <motion.button
                whileHover={{ x: 5 }}
                onClick={() => handleScroll("contact")}
                className="text-left transition bg-transparent border-none p-0 hover:text-[#F7941D] cursor-pointer"
              >
                Contact Us
              </motion.button>
            </li>

          </ul>
        </div>

        {/* Contact Section */}
        <div className="space-y-8">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="w-1 h-5 bg-[#F7941D]"></span>
              <p className="font-semibold text-white">EMAIL</p>
            </div>
            <div className="flex flex-col gap-1">
              <a href="mailto:connect@bytesfarms.co.in" className="text-sm text-gray-300 hover:underline">
                connect@bytesfarms.co.in
              </a>
              <a href="mailto:career@bytesfarms.co.in" className="text-sm text-gray-300 hover:underline">
                career@bytesfarms.co.in
              </a>
              <a href="mailto:hr@bytesfarms.co.in" className="text-sm text-gray-300 hover:underline">
                hr@bytesfarms.co.in
              </a>
            </div>
          </div>

          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="w-1 h-5 bg-[#F7941D]"></span>
              <p className="font-semibold text-white">INDIA</p>
              <Flag code="IN" style={{ width: 20, height: 15 }} />
            </div>
            <p className="text-sm text-gray-300 leading-relaxed">
              25, Patel Nagar, Opp Mohta Bagh, Aerodrome Road, Indore - 452005, MP
            </p>
          </div>
        </div>
      </div>

      <div className="mt-8 border-t border-gray-700 pt-6 text-center text-gray-400 text-sm w-full">
        Copyright © {new Date().getFullYear()} BytesFarms Technologies. All Rights Reserved
      </div>
    </motion.footer>
  );
};

export default Footer;